import type { Grinder, GrinderData, GrindersByBrand } from '@/types/grinder';

/**
 * Load grinder data from JSON file
 */
export async function loadGrinders(): Promise<GrinderData> {
  const response = await fetch('/data/grinders.json');
  if (!response.ok) {
    throw new Error(`Failed to load grinder data: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Organize grinders by brand
 */
export function groupByBrand(grinders: Grinder[]): GrindersByBrand {
  return grinders.reduce<GrindersByBrand>((acc, grinder) => {
    const brand = grinder.brand;
    if (!acc[brand]) {
      acc[brand] = [];
    }
    acc[brand].push(grinder);
    return acc;
  }, {});
}

/**
 * Get sorted list of brands
 */
export function getBrands(grindersByBrand: GrindersByBrand): string[] {
  return Object.keys(grindersByBrand).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );
}

/**
 * Get models for a specific brand, sorted alphabetically
 */
export function getModelsByBrand(grindersByBrand: GrindersByBrand, brand: string): Grinder[] {
  const grinders = grindersByBrand[brand] || [];
  return grinders.sort((a, b) =>
    a.model.localeCompare(b.model, undefined, { sensitivity: 'base' })
  );
}

/**
 * Find a grinder by brand and model
 */
export function findGrinder(
  grindersByBrand: GrindersByBrand,
  brand: string,
  model: string
): Grinder | undefined {
  const grinders = grindersByBrand[brand];
  if (!grinders) return undefined;
  return grinders.find(g => g.model === model);
}

/**
 * Calculate the position (in pixels) for a given micron value on the chart
 */
export function micronToPosition(
  micron: number,
  minMicron: number,
  maxMicron: number,
  chartWidth: number
): number {
  const range = maxMicron - minMicron;
  const normalized = (micron - minMicron) / range;
  return normalized * chartWidth;
}

/**
 * Calculate the micron value for a given position (in pixels) on the chart
 */
export function positionToMicron(
  position: number,
  minMicron: number,
  maxMicron: number,
  chartWidth: number
): number {
  const range = maxMicron - minMicron;
  const normalized = position / chartWidth;
  return minMicron + (normalized * range);
}

/**
 * Calculate micron value for a given tick index
 * Assumes linear interpolation between min and max microns
 */
export function tickToMicron(
  tickIndex: number,
  totalTicks: number,
  minMicron: number,
  maxMicron: number
): number {
  const range = maxMicron - minMicron;
  return minMicron + (tickIndex / (totalTicks - 1)) * range;
}

/**
 * Strip HTML tags from a string
 */
export function stripHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}
